import { Store } from 'redux';
import { History } from 'history';

// Utils
import { API_URL, Api, RequestType, correlateApi } from '@/utils/api';
import { ApiRequestConfig } from '@/utils/api';
import notification from '../utils/notification';
import { createCancellablePromise } from '../utils/promise';

// Redux
import { ApiAction, Result } from './index';
import { ReduxState } from './modules/types';
import { startLoaderAction, stopLoaderAction } from './modules/ui/loaders';
import { AUTHENTICATE_USER, PERSISTENCE_OAUTH2_CLEAR } from './modules/auth';
import Dictionary from '@/types/dictionary';
import Nullable from '@/types/nullable';
import AdvisorContext from '@/types/advisor-context';
import { AxiosHeaderValue } from 'axios';

// Don't show a notification automatically for the error keys
const ERROR_KEYS_WHITELIST = [
  'YOU_MUST_LOGIN',
  'BENCHMARK_NOT_FOUND',
  'COMPARISON_BENCHMARK_NOT_FOUND',
  'CANT_GET_ALL_BENCHMARKS',
  'INVALID_TOKEN',
  'TOKEN_DECODE_ERROR',
  'ACTIVITY_LOG_INVALID_VALUES',
  'ACTIVITY_LOG_UNSUPPORTED_TYPE',
  'QUESTIONNAIRE_ANSWERS_NOT_FOUND',
  'INVALID_LINK',
  'MFA_VERIFICATION_REQUIRED',
  'MFA_CONFIG_REQUIRED',
  'MULTIPLE_FAILED_LOGINS',
];

const SHELF_CODE_HEADER_NAME = 'X-Connect-Shelf-Code';
const CONTEXT_ID_HEADER_NAME = 'X-Connect-Context-Id';

class ReduxApi {
  api: Api;

  store?: Store;

  history?: History<unknown>;

  onSessionTimeout?: () => void;

  constructor(baseURL: string | undefined) {
    this.api = new Api(baseURL);
  }

  /**
   * Basic url parser, inject :params/ and append remaining as part of the query string
   * Url format: /api/test/:id/some/other/:property/
   */
  static _injectParams(url: string, params: Dictionary<any> | null) {
    const paramsBuffer: Dictionary<any> = { ...params };
    let newUrl = `${url}?`;

    for (let [key, value] of Object.entries(paramsBuffer)) {
      const paramKey = `:${key}/`;
      value = encodeURIComponent(value);

      if (newUrl.includes(paramKey)) {
        newUrl = newUrl.replace(paramKey, `${value}/`);
      } else {
        newUrl += `${key}=${value}&`;
      }
    }

    return newUrl.slice(0, -1);
  }

  _getConfigWithContext(config?: ApiRequestConfig) {
    let context: Nullable<AdvisorContext> = null;

    if (this.store != null) {
      const { auth } = this.store.getState() as ReduxState;
      context = auth.context;
    }

    const newConfig = config || {};

    if (context != null) {
      const newHeaders: Record<string, string | AxiosHeaderValue | undefined> = { ...newConfig.headers };
      if (context.advisor != null && context.advisor.dealerCode != null) {
        newHeaders[SHELF_CODE_HEADER_NAME] = context.advisor.dealerCode;
      }
      if (context.contextId != null) {
        newHeaders[CONTEXT_ID_HEADER_NAME] = context.contextId;
      }
      newConfig.headers = newHeaders;
    }
    return newConfig;
  }

  _dispatchSuccess(action: ApiAction, body: Dictionary<any> | undefined, params: Dictionary<any> | null = {}, response: { data: any }) {
    const data = action.transform ? action.transform(response) : response;

    this.store?.dispatch({ type: `${action.type}_SUCCESS`, data, payload: body, name: action.name, params });

    return data;
  }

  _request(
    method: RequestType,
    action: ApiAction,
    params: Dictionary<any> | null = {},
    body?: Dictionary<any>,
    config?: ApiRequestConfig,
    errorWhiteList?: string[],
  ) {
    const url = ReduxApi._injectParams(action.endpoint, params);

    const newConfig = this._getConfigWithContext(config);
    const request = [RequestType.Get, RequestType.Delete].includes(method) ? this.api[method](url, newConfig) : this.api[method](url, body, newConfig);
    const cancellable = createCancellablePromise(request);

    this.store?.dispatch(startLoaderAction(action.type, params as unknown as Dictionary<undefined>));
    this.store?.dispatch({ type: action.type, request: cancellable, payload: body, name: action.name, ...params });

    return cancellable.promise
      .then((response: { data: any }) => {
        const isGetMeEndpoint = action.endpoint === 'auth/me' && method === RequestType.Get;
        if (isGetMeEndpoint || action.endpoint === 'auth/login') {
          // if the endpoint is getMe or login, we need to set CSRF header before sending any subsequent requests
          return correlateApi
            .get('/csrf')
            .then(res => {
              const csrfToken = res.data.token;

              this.api.setCSRFDefaultHeaders(csrfToken);
              correlateApi.setCSRFDefaultHeaders(csrfToken);

              return this._dispatchSuccess(action, body, params, response);
            })
            .catch(err => {
              console.error(err);
              return this._dispatchSuccess(action, body, params, response);
            });
        }
        return this._dispatchSuccess(action, body, params, response);
      })
      .catch((error: { response?: any | undefined }) => {
        // Cancelled promises will always reject, so we'll exit out of the callback here.
        if (cancellable.hasCanceled()) {
          return;
        }

        const { response = {} } = error;
        this.store?.dispatch({ type: `${action.type}_ERROR`, data: response.data, payload: body, name: action.name, params });

        let swallowErrors = ERROR_KEYS_WHITELIST.slice();

        if (errorWhiteList) {
          swallowErrors = swallowErrors.concat(errorWhiteList);
        }

        // With 2FA there is a possibility that a session is created but the user
        // is not officially added to the store, we want to "partially" authenticate the user
        // when the user is missing 2FA verification
        if (response.status === 403 && response.data) {
          switch (response.data.errorKey) {
            case 'MFA_VERIFICATION_REQUIRED':
              this.store?.dispatch({ type: AUTHENTICATE_USER.type });
              this.history?.replace('/auth/2fa');
              break;
            case 'MFA_CONFIG_REQUIRED':
              this.store?.dispatch({ type: AUTHENTICATE_USER.type });
              this.history?.replace('/auth/2fa/setup', {
                from: this.history?.location.pathname,
                canSkipUntil: response.data.payload?.canSkipUntil,
              });
              break;
          }
        }

        if (response.data && response.data.errorKey === 'OAUTH2_REFRESH_FAILED') {
          this.store?.dispatch({ type: PERSISTENCE_OAUTH2_CLEAR.type });
          this.history?.replace('/auth');
        }

        if (response.data && response.data.errorKey === 'MULTIPLE_FAILED_LOGINS') {
          this.history?.replace('/auth/locked');
        }

        if (response.data && response.data.errorKey && !swallowErrors.includes(response.data.errorKey)) {
          if (response.data.errorKey === 'PERSISTENCE_EXTERNAL_PROVIDER' && response.data.payload?.showErrors) {
            notification.errorLong(response.data.error, response.data.payload.title);
          } else {
            notification.errorFromKey(response.data.errorKey);
          }
        }

        if (response.status === 401 && this.onSessionTimeout) {
          this.onSessionTimeout();
        }

        throw response;
      })
      .finally(() => {
        this.store?.dispatch(stopLoaderAction(action.type));
      });
  }

  registerStore(store: Store) {
    this.store = store;
  }

  registerSessionTimeoutAction(timeoutAction: () => Result<void>) {
    this.onSessionTimeout = () => {
      this.store?.dispatch<any>(timeoutAction());
    };
  }

  registerHistory(history: History) {
    this.history = history;
  }

  getHistory() {
    return this.history;
  }

  get(action: ApiAction, params?: Dictionary<any> | null, config?: ApiRequestConfig, errorWhiteList?: string[]) {
    return this._request(RequestType.Get, action, params, undefined, config, errorWhiteList);
  }

  put(action: ApiAction, params?: Dictionary<any> | null, body?: any, config?: ApiRequestConfig, errorWhiteList?: string[]) {
    return this._request(RequestType.Put, action, params, body, config, errorWhiteList);
  }

  patch(action: ApiAction, params?: Dictionary<any> | null, body?: any, config?: ApiRequestConfig, errorWhiteList?: string[]) {
    return this._request(RequestType.Patch, action, params, body, config, errorWhiteList);
  }

  post(action: ApiAction, params?: Dictionary<any> | null, body?: any, config?: ApiRequestConfig, errorWhiteList?: string[]) {
    return this._request(RequestType.Post, action, params, body, config, errorWhiteList);
  }

  delete(action: ApiAction, params?: Dictionary<any> | null, config?: ApiRequestConfig, errorWhiteList?: string[]) {
    return this._request(RequestType.Delete, action, params, undefined, config, errorWhiteList);
  }
}

const instance = new ReduxApi(API_URL);

export default instance;
